import React, { useState, useRef, useEffect } from 'react';
import { ShoppingCart, Check, ChevronDown } from 'lucide-react';
import { useGroceryLists } from '../../hooks/useGroceryLists';
import { useGuestList } from '../../hooks/useGuestList';
import { auth } from '../../services/firebase';

const safeListName = (name) => {
  if (typeof name === 'string') return name;
  if (name && typeof name === 'object') return name.name || name.text || 'List';
  return 'List';
};

const AddToListDropdown = ({ product, onSuccess }) => {
  const groceryData = useGroceryLists();
  const lists = groceryData?.lists ?? groceryData?.groceryLists ?? [];
  const defaultList = groceryData?.defaultList ?? (lists.length > 0 ? lists[0] : null);
  const addItemToList = groceryData?.addItemToList;

  const { guestList, addItem: addGuestItem } = useGuestList();
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const dropdownRef = useRef(null);

  const isGuest = !auth.currentUser;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddToList = async (list, e) => {
    e.stopPropagation();
    try {
      setAdding(true);
      const newItem = {
        name: product.name,
        quantity: 1,
        category: product.category || 'other',
        isChecked: false,
        sourceProductId: product.id,
        productImageURL: product.imageUrl || product.imageURL || null,
      };
      if (isGuest) {
        addGuestItem(newItem);
      } else {
        newItem.id = `${Date.now()}_${Math.random()}`;
        newItem.addedAt = new Date();
        await addItemToList(list.id, newItem);
      }
      setShowSuccess(true);
      setIsOpen(false);
      setTimeout(() => setShowSuccess(false), 2000);
      if (onSuccess) onSuccess(isGuest ? 'My Shopping List' : safeListName(list.name));
    } catch (error) {
      console.error('Error adding to list:', error);
      alert('Failed to add to list');
    } finally {
      setAdding(false);
    }
  };

  const handleQuickAdd = async (e) => {
    e.stopPropagation();
    if (isGuest) {
      await handleAddToList(guestList, e);
    } else if (defaultList) {
      await handleAddToList(defaultList, e);
    }
  };

  const displayList = isGuest ? guestList : defaultList;
  const allLists = isGuest ? (guestList ? [guestList] : []) : lists;

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex gap-xs">
        <button
          onClick={handleQuickAdd}
          disabled={adding || showSuccess || !displayList}
          className={`flex-1 flex items-center justify-center gap-sm px-md py-sm rounded-medium transition-all ${
            showSuccess
              ? 'bg-success text-white'
              : 'bg-primary text-white hover:bg-primary/90'
          } disabled:opacity-50`}
        >
          {showSuccess ? (
            <>
              <Check size={16} />
              <span className="text-caption font-semibold">Added!</span>
            </>
          ) : (
            <>
              <ShoppingCart size={16} />
              <span className="text-caption font-semibold">
                {displayList ? safeListName(displayList.name) : 'Add to List'}
              </span>
            </>
          )}
        </button>

        {!isGuest && lists.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
            className="w-8 h-8 rounded-medium bg-primary text-white hover:bg-primary/90 flex items-center justify-center transition-all"
          >
            <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {!isGuest && isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-xs bg-white rounded-medium shadow-strong border border-text-tertiary/20 py-sm z-20">
          <div className="max-h-48 overflow-y-auto">
            {allLists.length === 0 ? (
              <div className="px-md py-md text-center">
                <p className="text-caption text-text-secondary">No lists available</p>
              </div>
            ) : (
              allLists.map((list) => (
                <button
                  key={list.id}
                  onClick={(e) => handleAddToList(list, e)}
                  disabled={adding}
                  className="w-full text-left px-md py-sm hover:bg-primary/5 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <div className="flex items-center gap-sm">
                    <ShoppingCart size={14} className="text-primary" />
                    <span className="text-callout font-medium text-text-primary">
                      {safeListName(list.name)}
                    </span>
                  </div>
                  <span className="text-caption text-text-secondary">
                    {list.items?.length || 0} items
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddToListDropdown;
